import React, { useEffect, useState } from 'react';
import { GitBranch, ShieldCheck, CircleDot, GripVertical } from 'lucide-react';
import type { PrSummary } from '../lib/data';
import { Skeleton, SkeletonKanbanColumn } from './Skeleton';

/**
 * Simple Technical Lead task board: 4 statuses. PRs are bucketed by a mapper
 * over GitHub status + review conclusion, so an operator can triage at a
 * glance. Manual drag-and-drop can pin a card to any status (see OVERRIDE_KEY).
 *
 *   TODO        — open, not yet reviewed.
 *   IN-PROGRESS — reviewed but not approved (changes likely in flight).
 *   REVIEW      — approved, awaiting merge.
 *   DONE        — merged or closed.
 */
const STAGES = [
  'TODO',
  'IN-PROGRESS',
  'REVIEW',
  'DONE',
] as const;
type Stage = (typeof STAGES)[number];

const STAGE_ACCENT: Record<Stage, string> = {
  'TODO': '#91918E',
  'IN-PROGRESS': '#2383E2',
  'REVIEW': '#D9730D',
  'DONE': '#2E7D32',
};

function stageOf(pr: PrSummary): Stage {
  if (pr.status === 'merged' || pr.status === 'closed') return 'DONE';
  if (pr.reviewStatus?.conclusion === 'approved') return 'REVIEW';
  // Reviewed or a review ran but wasn't approved → iterating. Covers the edge
  // case where status flipped to 'reviewed' without a populated reviewStatus.
  if (pr.reviewStatus || pr.status === 'reviewed') return 'IN-PROGRESS';
  return 'TODO';
}

/**
 * Manual stage overrides from drag-and-drop. PRs are GitHub-driven and the
 * statuses above are *derived* from status + reviewStatus, so dropping a card
 * into a column writes a client-side override pinned in localStorage — the card
 * then stays put instead of snapping back to its derived stage.
 *
 * The key is versioned (v2): the stage set changed from the 7-stage SDLC
 * pipeline to the 4 TL statuses, so any v1 override (a stale stage name) would
 * crash the bucket lookup. Bumping the key drops old overrides and re-derives.
 *
 * `ponytail:` override is client-side only (survives refresh, resets if localStorage
 * is cleared). Add a `stage_override` D1 column + `PATCH /api/prs/:id/stage` when
 * server-side workflow status is needed; until then `stageOf` stays the source of
 * truth for everyone but the operator who dragged the card.
 */
const OVERRIDE_KEY = 'nim-task-stage-overrides-v2';

interface KanbanBoardProps {
  prs: PrSummary[];
  onSelectPr: (id: string) => void;
  loading: boolean;
}

export function KanbanBoard({ prs, onSelectPr, loading }: KanbanBoardProps) {
  // Manual stage overrides from drag-and-drop (see OVERRIDE_KEY note above).
  const [overrides, setOverrides] = useState<Record<string, Stage>>(() => {
    try { return JSON.parse(localStorage.getItem(OVERRIDE_KEY) ?? '{}'); } catch { return {}; }
  });
  const [dragOver, setDragOver] = useState<Stage | null>(null);
  useEffect(() => {
    try { localStorage.setItem(OVERRIDE_KEY, JSON.stringify(overrides)); } catch { /* private mode */ }
  }, [overrides]);

  const buckets: Record<Stage, PrSummary[]> = {
    'TODO': [], 'IN-PROGRESS': [], 'REVIEW': [], 'DONE': [],
  };
  for (const pr of prs) {
    const s = overrides[pr.id] ?? stageOf(pr);
    // Guard buckets[s]: an override from a future/changed stage set (or a
    // corrupted localStorage entry) must never crash the render.
    if (s && buckets[s]) buckets[s].push(pr);
  }

  const handleDrop = (prId: string, stage: Stage) => {
    setOverrides((prev) => (prev[prId] === stage ? prev : { ...prev, [prId]: stage }));
    setDragOver(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Tasks</h1>
          <p className="text-xs text-muted-foreground">
            {prs.length} tasks
          </p>
        </div>
      </div>

      {loading ? (
        <div aria-busy="true" aria-label="Loading pipeline">
          <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width={140} height={18} rounded={5} />
            <Skeleton width={100} height={10} rounded={3} />
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STAGES.map((s) => (
              <SkeletonKanbanColumn key={s} cards={[2, 3, 1, 2][STAGES.indexOf(s)]} />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map((stage) => (
            <div key={stage} className="flex-1 min-w-[120px]">
              <div className="flex items-center gap-2 mb-2 px-1 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STAGE_ACCENT[stage] }} />
                <span className="text-xs font-semibold uppercase tracking-wide truncate min-w-0 flex-1 text-foreground">
                  {stage}
                </span>
                <span className="text-[11px] shrink-0 text-muted-foreground">
                  {buckets[stage].length}
                </span>
              </div>

              <div
                className="rounded-lg p-2 space-y-2 min-h-[120px] transition bg-muted"
                style={{
                  outline: dragOver === stage ? `2px dashed ${STAGE_ACCENT[stage]}` : '2px dashed transparent',
                  outlineOffset: -2,
                }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOver !== stage) setDragOver(stage); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const prId = e.dataTransfer.getData('text/plain');
                  if (prId) handleDrop(prId, stage);
                }}
              >
                {buckets[stage].length === 0 && (
                  <div className="text-[11px] text-center py-6 truncate text-muted-foreground">
                    No items
                  </div>
                )}
                {buckets[stage].map((pr) => (
                  <button
                    key={pr.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', pr.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => setDragOver(null)}
                    onClick={() => onSelectPr(pr.id)}
                    className="group w-full text-left rounded-md p-2.5 transition cursor-grab active:cursor-grabbing hover:shadow-sm bg-card border border-border"
                  >
                    <div className="flex items-start gap-1">
                      <div className="text-xs font-medium leading-snug break-words line-clamp-3 flex-1 text-foreground">
                        {pr.title}
                      </div>
                      <GripVertical
                        className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-60 transition text-muted-foreground"
                        aria-hidden
                      />
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] min-w-0 text-muted-foreground">
                      {pr.authorAvatar ? (
                        <img src={pr.authorAvatar} alt="" className="w-3.5 h-3.5 rounded-full shrink-0" />
                      ) : (
                        <CircleDot className="w-3 h-3 shrink-0" />
                      )}
                      <span className="truncate">{pr.author}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-mono min-w-0 max-w-full bg-muted text-muted-foreground">
                        <GitBranch className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{pr.branch}</span>
                      </span>
                      {pr.reviewStatus && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 bg-primary/10 text-primary">
                          <ShieldCheck className="w-2.5 h-2.5" />
                          {pr.reviewStatus.score ?? '—'}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
